import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { GetStartedDialog } from '@/components/landing/GetStartedDialog';
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Lock,
  ShieldCheck,
  EyeOff,
  MessagesSquare,
  Sparkles,
  Compass,
  Menu,
  Quote,
  Users,
  Building2,
  Globe2,
} from 'lucide-react';
import ivLogomark from '@/assets/iv-talent-logomark.png';

/* -------------------------------------------------------------- */
/*  Small visual building blocks                                  */
/* -------------------------------------------------------------- */

function Waveform({ className = '', bars = 9 }: { className?: string; bars?: number }) {
  return (
    <span className={`waveform ${className}`} aria-hidden="true">
      {Array.from({ length: bars }).map((_, i) => (
        <span key={i} />
      ))}
    </span>
  );
}

function FlowLine({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`flowline ${className}`}
      viewBox="0 0 1200 200"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="flow-grad" x1="0" x2="1">
          <stop offset="0%" stopColor="hsl(var(--brand))" stopOpacity="0" />
          <stop offset="50%" stopColor="hsl(var(--brand))" stopOpacity="0.7" />
          <stop offset="100%" stopColor="hsl(var(--brand))" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0,100 Q150,20 300,100 T600,100 T900,100 T1200,100"
        fill="none"
        stroke="url(#flow-grad)"
        strokeWidth="1.5"
      />
      <path
        d="M0,120 Q150,60 300,120 T600,120 T900,120 T1200,120"
        fill="none"
        stroke="url(#flow-grad)"
        strokeWidth="1"
        opacity="0.5"
      />
    </svg>
  );
}

function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            (e.target as HTMLElement).classList.add('is-visible');
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Floating conversation + insight cards (hero visual)           */
/* -------------------------------------------------------------- */

const REPLY_TEXT =
  "Honestly? Too many priorities and no one will say what's most important.";

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function ConversationCard() {
  const [typed, setTyped] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setTyped(REPLY_TEXT);
      setDone(true);
      return;
    }
    let i = 0;
    const step = 28; // ms per character
    const id = window.setInterval(() => {
      i += 1;
      setTyped(REPLY_TEXT.slice(0, i));
      if (i >= REPLY_TEXT.length) {
        window.clearInterval(id);
        // hold caret briefly, then fade
        window.setTimeout(() => setDone(true), 350);
      }
    }, step);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="card-soft rounded-2xl bg-white/95 border border-hairline p-5 max-w-sm backdrop-blur-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-7 w-7 rounded-full bg-ink flex items-center justify-center text-cream text-[10px] font-grotesk font-semibold">
          C
        </div>
        <div className="text-xs font-grotesk text-charcoal/70">
          Charles · live conversation
        </div>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-grotesk text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          Private
        </span>
      </div>
      <div className="space-y-3 text-[13px] font-grotesk leading-relaxed">
        <div className="bg-cream-warm rounded-2xl rounded-tl-sm px-3 py-2 text-charcoal">
          What's actually getting in the way of doing your best work right now?
        </div>
        <div className="bg-ink text-cream rounded-2xl rounded-tr-sm px-3 py-2 ml-8" aria-live="polite">
          {typed}
          <span className={`type-caret ${done ? 'is-done' : ''}`} aria-hidden="true" />
        </div>
        <div className="bg-cream-warm rounded-2xl rounded-tl-sm px-3 py-2 text-charcoal">
          Tell me about the last time that happened.
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 text-charcoal/60">
        <Waveform className="text-brand h-5" />
        <span className="text-[11px] font-grotesk">listening…</span>
      </div>
    </div>
  );
}

function InsightCard() {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (prefersReducedMotion()) {
      setArmed(true);
      return;
    }
    // Wait for the card's slide-in (~1.8s parent reveal delay + ~350ms ease) before filling bars.
    const id = window.setTimeout(() => setArmed(true), 2150);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <div className="card-soft rounded-2xl bg-ink text-cream p-5 max-w-xs border border-white/5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-widest text-cream/60 font-grotesk">
          Theme · this week
        </span>
        <Sparkles className="h-3.5 w-3.5 text-brand" />
      </div>
      <p className="font-display text-xl leading-snug mb-4">
        "Conflicting priorities" mentioned in 38% of conversations.
      </p>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-[11px] font-grotesk">
          <span className="text-cream/70">Engineering</span>
          <span className="text-cream">52%</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div
            className="bar-fill h-full rounded-full bg-brand"
            style={{ width: armed ? '52%' : '0%', transitionDelay: '0ms' }}
          />
        </div>
        <div className="flex items-center justify-between text-[11px] font-grotesk pt-1">
          <span className="text-cream/70">Operations</span>
          <span className="text-cream">31%</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div
            className="bar-fill h-full rounded-full bg-brand/70"
            style={{ width: armed ? '31%' : '0%', transitionDelay: '120ms' }}
          />
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */
/*  Nav                                                           */
/* -------------------------------------------------------------- */

function Logo({ tone = 'dark' }: { tone?: 'dark' | 'light' }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 group">
      <img
        src={ivLogomark}
        alt="IV Talent"
        className="h-8 w-8 rounded-md"
      />
      <span
        className={`font-display text-[20px] tracking-tight ${
          tone === 'dark' ? 'text-ink' : 'text-cream'
        }`}
      >
        CultureLens
      </span>
    </Link>
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { href: '#product', label: 'Product' },
    { href: '#how', label: 'How it works' },
    { href: '#privacy', label: 'Privacy' },
    { href: '#customers', label: 'Customers' },
    { href: '#pricing', label: 'Pricing' },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'nav-glass py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-content mx-auto px-6 flex items-center justify-between">
        <Logo />

        <nav className="hidden md:flex items-center gap-8 text-sm font-grotesk text-charcoal/80">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="hover:text-ink transition-colors focus:outline-none focus-visible:underline underline-offset-4"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Button asChild variant="ghost" className="text-charcoal hover:text-ink hover:bg-transparent">
            <Link to="/auth/signin">Sign in</Link>
          </Button>
          <GetStartedDialog>
            <Button className="bg-ink hover:bg-ink-soft text-cream rounded-full px-5 h-10 font-grotesk">
              Request Demo
            </Button>
          </GetStartedDialog>
        </div>

        {/* Mobile */}
        <Sheet>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-cream border-l border-hairline w-[85%]">
            <div className="mt-8 flex flex-col gap-1">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  className="px-3 py-3 rounded-lg text-charcoal font-grotesk hover:bg-cream-warm"
                >
                  {l.label}
                </a>
              ))}
              <div className="h-px bg-hairline my-4" />
              <Link
                to="/auth/signin"
                className="px-3 py-3 rounded-lg text-charcoal font-grotesk hover:bg-cream-warm"
              >
                Sign in
              </Link>
              <GetStartedDialog>
                <Button className="mt-2 bg-ink hover:bg-ink-soft text-cream rounded-full h-11 font-grotesk">
                  Request Demo
                </Button>
              </GetStartedDialog>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------- */
/*  Sections                                                      */
/* -------------------------------------------------------------- */

function Hero() {
  return (
    <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden hero-gradient hero-noise">
      <div className="absolute inset-x-0 top-[28%] -z-0 pointer-events-none">
        <div className="hero-waveline opacity-[0.07]">
          <FlowLine className="w-full h-40 text-brand" />
        </div>
      </div>
      <div className="relative max-w-content mx-auto px-6 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-grotesk text-charcoal/70 bg-white/60 border border-hairline rounded-full px-3 py-1.5">
              <Waveform className="text-brand h-3" bars={5} />
              Culture intelligence platform
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="font-display text-[44px] sm:text-[56px] md:text-[72px] leading-[1.02] text-ink mt-6">
              Stop ticking boxes.
              <br />
              <span className="italic text-brand">Start talking.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 max-w-xl text-lg md:text-xl text-charcoal/80 font-grotesk leading-relaxed">
              CultureLens helps you understand what your people actually think
              and feel — and turn that into measurable change.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <GetStartedDialog>
                <Button className="bg-ink hover:bg-ink-soft text-cream rounded-full h-12 px-6 font-grotesk text-[15px]">
                  Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </GetStartedDialog>
              <Button
                asChild
                variant="ghost"
                className="rounded-full h-12 px-5 text-charcoal hover:text-ink hover:bg-white/50 font-grotesk text-[15px]"
              >
                <a href="#how">
                  See how it works <ArrowUpRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
            </div>
          </Reveal>
          <Reveal delay={320}>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-grotesk text-charcoal/70">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-brand" /> Private by design
              </span>
              <span className="hidden sm:inline text-charcoal/30">·</span>
              <span className="inline-flex items-center gap-1.5">
                <EyeOff className="h-3.5 w-3.5 text-brand" /> Anonymous by default
              </span>
              <span className="hidden sm:inline text-charcoal/30">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5 text-brand" /> Built for Flexibility
              </span>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-5 relative h-[440px] hidden md:block">
          <Reveal delay={0} className="absolute top-2 right-6">
            <ConversationCard />
          </Reveal>
          <Reveal delay={1800} className="absolute bottom-2 left-0">
            <InsightCard />
          </Reveal>
          <div className="absolute inset-0 -z-10 blur-3xl opacity-60 bg-gradient-to-br from-brand/30 via-transparent to-amber-200/30 rounded-full" />
        </div>

        {/* Mobile cards stacked */}
        <div className="md:hidden grid gap-4 mt-2">
          <ConversationCard />
          <InsightCard />
        </div>
      </div>
    </section>
  );
}

function FoundingPartners() {
  const tiles = [
    'A Family-Owned Private Practice',
    'A 150-Year-Old Local Engineering Company',
    'A Locally Owned Restaurant & Entertainment Group',
  ];
  return (
    <section id="customers" className="bg-cream border-y border-hairline py-16 md:py-20">
      <div className="max-w-content mx-auto px-6">
        <p className="text-center text-[11px] uppercase tracking-[0.22em] font-grotesk text-charcoal/60 mb-8">
          Founding Partners · Under NDA
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 max-w-4xl mx-auto">
          {tiles.map((t) => (
            <div
              key={t}
              className="rounded-md border border-hairline bg-white/60 px-4 py-4 text-center text-[10px] md:text-[11px] uppercase tracking-[0.18em] font-grotesk text-charcoal/65 leading-snug flex items-center justify-center"
            >
              {t}
            </div>
          ))}
        </div>
        <p className="mt-8 text-center font-grotesk text-sm text-charcoal/60 max-w-2xl mx-auto leading-relaxed">
          We're working with a small group of early organizations to shape what culture
          intelligence should be.
        </p>
      </div>
    </section>
  );
}

function Problem() {
  const items = [
    {
      title: 'Once a year is too late.',
      body: 'Culture moves weekly. An annual snapshot tells you what you missed, not what to do.',
    },
    {
      title: "Scores don't explain why.",
      body: "A 6.4 out of 10 isn't an insight. It's a number waiting for context that never comes.",
    },
    {
      title: "People don't tell the truth to a form.",
      body: 'They tell the truth to someone who listens. So we built someone who listens.',
    },
  ];
  return (
    <section className="bg-cream py-24 md:py-32">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl text-ink max-w-3xl leading-[1.05]">
            The annual survey
            <br />
            <span className="italic text-charcoal/50">is broken.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-3 gap-10 md:gap-12">
          {items.map((it, i) => (
            <Reveal key={it.title} delay={i * 120}>
              <div className="border-t border-hairline pt-6">
                <div className="h-10 w-10 rounded-full bg-ink text-cream flex items-center justify-center font-grotesk text-sm mb-5">
                  0{i + 1}
                </div>
                <h3 className="font-display text-2xl text-ink mb-3 leading-snug">
                  {it.title}
                </h3>
                <p className="font-grotesk text-charcoal/75 leading-relaxed">
                  {it.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Comparison() {
  const rows = [
    { label: 'Frequency', old: 'Once a year', neu: 'Always on' },
    { label: 'Format', old: 'Multiple choice', neu: 'Real conversation' },
    { label: 'Honesty', old: 'Filtered, performative', neu: 'Anonymous, candid' },
    { label: 'Depth of insight', old: 'Scores without context', neu: 'Themes with quotes & cause' },
    { label: 'Time to action', old: '3–6 months', neu: 'Weeks' },
  ];
  return (
    <section className="bg-cream-warm py-24 md:py-32">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.2em] font-grotesk text-brand mb-4">
            The shift
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-ink max-w-3xl leading-[1.08]">
            A different kind of listening, by design.
          </h2>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-12 grid md:grid-cols-[1fr_1.4fr] gap-4 md:gap-6">
            {/* Old column */}
            <div className="rounded-2xl border border-hairline bg-white/60 p-6 md:p-8">
              <div className="text-[11px] uppercase tracking-[0.18em] font-grotesk text-charcoal/50 mb-6">
                Traditional engagement survey
              </div>
              <ul className="space-y-5">
                {rows.map((r) => (
                  <li key={r.label}>
                    <div className="text-[11px] uppercase tracking-wider text-charcoal/45 font-grotesk mb-1">
                      {r.label}
                    </div>
                    <div className="font-display text-lg text-charcoal/70 line-through decoration-charcoal/30">
                      {r.old}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* CultureLens column */}
            <div className="rounded-2xl bg-ink text-cream p-6 md:p-10 relative overflow-hidden card-soft">
              <div className="absolute -top-20 -right-20 h-64 w-64 bg-brand/20 rounded-full blur-3xl" />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.18em] font-grotesk text-brand mb-6 flex items-center gap-2">
                  <Waveform className="text-brand h-3" bars={5} /> CultureLens
                </div>
                <ul className="space-y-5">
                  {rows.map((r) => (
                    <li key={r.label}>
                      <div className="text-[11px] uppercase tracking-wider text-cream/50 font-grotesk mb-1">
                        {r.label}
                      </div>
                      <div className="font-display text-2xl text-cream">
                        {r.neu}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Pillars() {
  const items = [
    {
      icon: MessagesSquare,
      eyebrow: 'Conversational listening',
      title: 'Talk, not tick.',
      body:
        'Natural voice or chat conversations that adapt to each person and uncover what a survey never could.',
      visual: (
        <div className="rounded-xl bg-cream-warm border border-hairline p-4 space-y-2">
          <div className="text-[11px] font-grotesk bg-white rounded-lg px-3 py-2 text-charcoal">
            What does great look like on your team?
          </div>
          <div className="text-[11px] font-grotesk bg-ink text-cream rounded-lg px-3 py-2 ml-6">
            When we finish a sprint and actually ship something useful…
          </div>
          <Waveform className="text-brand h-4 mt-2" bars={9} />
        </div>
      ),
    },
    {
      icon: Compass,
      eyebrow: 'Gap analysis',
      title: 'Stated vs lived.',
      body:
        'See exactly where the lived experience diverges from your stated culture and strategy — by team, by theme, over time.',
      visual: (
        <div className="rounded-xl bg-cream-warm border border-hairline p-4">
          <div className="grid grid-cols-2 gap-3 text-[10px] font-grotesk">
            <div>
              <div className="text-charcoal/60 mb-1">Stated</div>
              <div className="h-2 rounded-full bg-charcoal/20 overflow-hidden">
                <div className="h-full w-[90%] bg-ink" />
              </div>
              <div className="text-charcoal mt-1 font-medium">"We move fast."</div>
            </div>
            <div>
              <div className="text-charcoal/60 mb-1">Lived</div>
              <div className="h-2 rounded-full bg-brand/15 overflow-hidden">
                <div className="h-full w-[42%] bg-brand" />
              </div>
              <div className="text-charcoal mt-1 font-medium">"Approvals stall us."</div>
            </div>
          </div>
        </div>
      ),
    },
    {
      icon: Sparkles,
      eyebrow: 'Actionable insight',
      title: 'Themes you can act on.',
      body:
        'Themes, signals, and recommendations grounded in real employee voices — ready to act on in weeks, not months.',
      visual: (
        <div className="rounded-xl bg-cream-warm border border-hairline p-4 space-y-2">
          {[
            { t: 'Unclear priorities', v: 38 },
            { t: 'Manager 1:1 cadence', v: 24 },
            { t: 'Tooling friction', v: 17 },
          ].map((row) => (
            <div key={row.t} className="flex items-center gap-3 text-[11px] font-grotesk">
              <span className="w-28 text-charcoal">{row.t}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white overflow-hidden">
                <div className="h-full bg-brand" style={{ width: `${row.v * 2}%` }} />
              </div>
              <span className="text-charcoal/60 w-8 text-right">{row.v}%</span>
            </div>
          ))}
        </div>
      ),
    },
  ];

  return (
    <section id="product" className="bg-cream py-24 md:py-32">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.2em] font-grotesk text-brand mb-4">
            What you get
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-ink max-w-3xl leading-[1.08]">
            Three things every culture leader has always needed.
          </h2>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <Reveal key={it.title} delay={i * 120}>
              <article className="h-full rounded-2xl bg-white border border-hairline p-7 card-soft flex flex-col">
                <div className="h-10 w-10 rounded-lg bg-ink text-cream flex items-center justify-center mb-5">
                  <it.icon className="h-5 w-5" />
                </div>
                <div className="text-[11px] uppercase tracking-[0.16em] font-grotesk text-charcoal/55 mb-2">
                  {it.eyebrow}
                </div>
                <h3 className="font-display text-2xl text-ink mb-3 leading-snug">
                  {it.title}
                </h3>
                <p className="font-grotesk text-charcoal/75 leading-relaxed">
                  {it.body}
                </p>
                <div className="mt-6">{it.visual}</div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: '01',
      title: 'Share your vision.',
      body: 'Tell us where you are taking your team, department, or organization. We frame every conversation around that.',
      visual: (
        <div className="rounded-2xl border border-hairline bg-cream-warm p-6 card-soft">
          <div className="text-[11px] uppercase tracking-widest font-grotesk text-charcoal/50 mb-3">
            Setup · culture brief
          </div>
          <div className="font-display text-xl text-ink leading-snug">
            "We want a culture where people challenge each other openly and ship weekly."
          </div>
          <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-grotesk">
            {['Candor', 'Velocity', 'Ownership'].map((t) => (
              <span key={t} className="px-2.5 py-1 rounded-full bg-white border border-hairline text-charcoal">
                {t}
              </span>
            ))}
          </div>
        </div>
      ),
    },
    {
      n: '02',
      title: 'Discuss the challenges.',
      body: 'Describe what is in the way — quietly, between us. We shape interviews around what actually matters to you right now.',
      visual: (
        <div className="rounded-2xl border border-hairline bg-cream-warm p-6 card-soft space-y-3">
          {['Aligning a newly merged org', 'Mid-tenure retention', 'Manager confidence after restructure'].map((c) => (
            <div key={c} className="flex items-center gap-3 text-sm font-grotesk text-charcoal">
              <Check className="h-4 w-4 text-brand" />
              {c}
            </div>
          ))}
        </div>
      ),
    },
    {
      n: '03',
      title: 'Conversations begin.',
      body: 'Charles speaks one-to-one with 10 or 10,000 of your people, on their schedule, in their words.',
      visual: <ConversationCard />,
    },
    {
      n: '04',
      title: 'Insights surface.',
      body: 'Themes, gaps, and recommendations — grounded in real voices, ready to act on.',
      visual: <InsightCard />,
    },
  ];

  return (
    <section id="how" className="bg-cream-warm py-24 md:py-32">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.2em] font-grotesk text-brand mb-4">
            How it works
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-ink max-w-3xl leading-[1.08]">
            From quiet conversation to confident decision.
          </h2>
        </Reveal>

        <div className="mt-16 space-y-20 md:space-y-28">
          {steps.map((s, i) => (
            <Reveal key={s.n}>
              <div
                className={`grid md:grid-cols-12 gap-10 items-center ${
                  i % 2 === 1 ? 'md:[&>*:first-child]:order-2' : ''
                }`}
              >
                <div className="md:col-span-6">
                  <div className="font-display text-6xl md:text-7xl text-brand/60 leading-none mb-4">
                    {s.n}
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl text-ink leading-[1.1] mb-4">
                    {s.title}
                  </h3>
                  <p className="font-grotesk text-charcoal/75 text-lg leading-relaxed max-w-md">
                    {s.body}
                  </p>
                </div>
                <div className="md:col-span-6">
                  <div className="md:max-w-md md:ml-auto">{s.visual}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Flexibility() {
  const cards = [
    {
      icon: Users,
      title: 'Small, high-stakes teams',
      chip: '7–50 people',
      body: "Perfect for leadership teams, executive committees, and tight-knit departments. Charles draws out the things people won't say in the room — and you get a real read on alignment, trust, and execution risk in days.",
      footnote: 'Privacy floor (k ≥ 5) is preserved on every view.',
    },
    {
      icon: Building2,
      title: 'Departments and business units',
      chip: '50–500 people',
      body: 'Run CultureLens across a single function or business unit. Compare themes by team, location, or tenure — always aggregated, always private — and see exactly where the lived culture is diverging from the stated one.',
    },
    {
      icon: Globe2,
      title: 'Whole-organization rollout',
      chip: '500–10,000+ people',
      body: 'Multi-language, multi-persona Field Advisors, segmentation by any dimension that matters, and one aggregated view of the whole culture. Distributed, hybrid, frontline, deskless — everyone is heard.',
    },
  ];
  return (
    <section id="flexibility" className="bg-cream py-24 md:py-32 border-t border-hairline">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.2em] font-grotesk text-brand mb-4">
              Built for flexibility
            </p>
            <h2 className="font-display text-4xl md:text-6xl text-ink leading-[1.05]">
              From a leadership team of seven
              <br />
              <span className="italic text-charcoal/60">to a workforce of ten thousand.</span>
            </h2>
            <p className="mt-6 font-grotesk text-charcoal/75 text-lg leading-relaxed max-w-2xl">
              CultureLens adapts to the shape of your organization. Run it for one team, one
              department, or the whole company — with the same depth of insight at every scale.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.title} delay={i * 120}>
                <div className="card-soft h-full rounded-2xl border border-hairline bg-white p-8 flex flex-col">
                  <div className="h-11 w-11 rounded-xl bg-brand/10 text-brand flex items-center justify-center mb-6">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-display text-2xl text-ink leading-snug mb-3">{c.title}</h3>
                  <span className="self-start inline-flex items-center text-[10px] uppercase tracking-[0.18em] font-grotesk text-brand border border-brand/30 bg-brand/5 rounded-full px-2.5 py-1 mb-5">
                    {c.chip}
                  </span>
                  <p className="font-grotesk text-charcoal/80 text-[15px] leading-relaxed flex-1">
                    {c.body}
                  </p>
                  {c.footnote && (
                    <p className="mt-6 pt-5 border-t border-hairline font-grotesk text-xs text-charcoal/55 leading-relaxed">
                      {c.footnote}
                    </p>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        <p className="mt-10 text-center font-grotesk text-sm text-charcoal/55">
          Same platform. Same privacy guarantees. Different lens for every scale.
        </p>
      </div>
    </section>
  );
}

function Privacy() {
  const items = [
    {
      icon: MessagesSquare,
      title: 'Confidential conversations.',
      body:
        'Individual conversations and pulses are never shown to anyone in your organization — not even admins. Privacy is enforced at the data layer, not by policy.',
    },
    {
      icon: EyeOff,
      title: 'Aggregated-only reporting.',
      body:
        'Themes, breakdowns, and trends are suppressed below a 5-person floor (k≥5). Leaders see patterns across many voices — never a single person.',
    },
    {
      icon: ShieldCheck,
      title: 'Enterprise-grade protection.',
      body:
        'Identity is structurally isolated from content using per-organization safeguards. Every admin action runs through verified, audited, server-side controls.',
    },
  ];

  return (
    <section id="privacy" className="relative bg-ink text-cream py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <FlowLine className="w-full h-full text-brand" />
      </div>
      <div className="relative max-w-content mx-auto px-6">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.2em] font-grotesk text-brand mb-4">
            Trust model
          </p>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-3xl">
            Private by design.
            <br />
            <span className="italic text-cream/70">Anonymous by default.</span>
          </h2>
          <p className="mt-6 font-grotesk text-cream/70 max-w-2xl text-lg leading-relaxed">
            People only open up if they trust the system. So trust is built into the
            foundation — not promised in a policy.
          </p>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <Reveal key={it.title} delay={i * 100}>
              <div className="h-full rounded-2xl bg-white/[0.03] border border-white/10 p-7 backdrop-blur-sm">
                <div className="h-10 w-10 rounded-lg bg-brand/15 text-brand flex items-center justify-center mb-5">
                  <it.icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-2xl mb-3 leading-snug">{it.title}</h3>
                <p className="font-grotesk text-cream/70 leading-relaxed">{it.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={300}>
          <div className="mt-12 rounded-2xl border border-brand/30 bg-brand/[0.06] p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
            <div className="h-12 w-12 rounded-full bg-brand text-ink flex items-center justify-center shrink-0">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-xl mb-1">Our confidentiality promise</div>
              <p className="font-grotesk text-cream/80 leading-relaxed">
                Leaders get the truth about their culture. Employees keep their privacy.
                Neither has to take the other's word for it — the platform enforces it.
              </p>
            </div>
            <Link
              to="/privacy"
              className="md:ml-auto inline-flex items-center gap-1 font-grotesk text-sm text-cream hover:text-brand transition-colors shrink-0"
            >
              Read the full policy <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Stats() {
  const stats = [
    {
      big: 'Daily',
      label: 'pulses, not annual surveys.',
      support: 'Always-on listening, every day of the year.',
    },
    {
      big: '<2 wks',
      label: 'from kickoff to live.',
      support: 'Most teams are up and running in under two weeks.',
    },
    {
      big: 'k ≥ 5',
      label: 'privacy floor on every view.',
      support: 'No report ever represents fewer than five people.',
    },
  ];
  return (
    <section className="bg-cream py-24 md:py-32 border-y border-hairline">
      <div className="max-w-content mx-auto px-6 grid md:grid-cols-3 gap-12 md:gap-6">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 120}>
            <div className="md:px-6 md:border-l md:first:border-l-0 border-hairline">
              <div className="font-display text-6xl md:text-7xl text-ink leading-none mb-4">
                {s.big}
              </div>
              <p className="font-grotesk text-ink/90 font-medium max-w-[22ch] leading-snug mb-2">
                {s.label}
              </p>
              <p className="font-grotesk text-charcoal/65 text-sm max-w-[28ch] leading-relaxed">
                {s.support}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}


function Manifesto() {
  return (
    <section className="bg-cream-warm py-24 md:py-32">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center mb-8">
              <div className="h-12 w-12 rounded-md bg-ink text-cream flex items-center justify-center font-display text-lg tracking-tight">
                IV
              </div>
            </div>
            <h2 className="font-display text-4xl md:text-6xl text-ink leading-[1.05] text-center">
              Why we built CultureLens.
            </h2>
            <div className="mt-10 space-y-6 font-grotesk text-charcoal/80 text-lg md:text-xl leading-[1.7] max-w-3xl mx-auto">
              <p>
                For twenty years we've watched organizations spend millions on annual surveys that
                tell them what they already suspect, six months too late. People don't lie on
                surveys — they just round the corners. They protect their boss, their team, their
                job.
              </p>
              <p>
                CultureLens was built to remove that friction. Charles is a Field Advisor your
                people can actually talk to — privately, in their own words, on their own time.
                Every conversation aggregates into themes you can act on this week, not next year.
              </p>
              <p>
                We're in our founding chapter. A handful of early partners are helping us shape
                this. If that's interesting to your organization, we'd like to talk.
              </p>
            </div>
            <p className="mt-10 text-center font-grotesk text-sm text-charcoal/60">
              — The CultureLens Team
            </p>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-20 max-w-4xl mx-auto">
            <p className="text-center text-[11px] uppercase tracking-[0.22em] font-grotesk text-charcoal/60 mb-6">
              Founding Partners · Under NDA
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {[
                'A Family-Owned Private Practice',
                'A 150-Year-Old Local Engineering Company',
                'A Locally Owned Restaurant & Entertainment Group',
              ].map((t) => (
                <div
                  key={t}
                  className="rounded-md border border-hairline bg-white/70 px-4 py-4 text-center text-[10px] md:text-[11px] uppercase tracking-[0.18em] font-grotesk text-charcoal/65 leading-snug flex items-center justify-center"
                >
                  {t}
                </div>
              ))}
            </div>
            <p className="mt-6 text-center font-grotesk text-sm text-charcoal/60 max-w-2xl mx-auto leading-relaxed">
              We're working with a small group of early organizations to shape what culture
              intelligence should be.
            </p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="relative rounded-2xl border border-hairline bg-cream overflow-hidden card-soft">
              <div className="absolute inset-y-0 left-0 w-1 bg-brand" aria-hidden="true" />
              <div className="p-8 md:p-10 pl-10 md:pl-12 grid md:grid-cols-[1.2fr_1fr] gap-8 md:gap-10 items-center">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] font-grotesk text-brand mb-4">
                    Founding Cohort · Limited Spots
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl text-ink leading-snug">
                    Help shape the future of culture intelligence.
                  </h3>
                  <ul className="mt-6 space-y-3">
                    {[
                      'Locked-in founding pricing for the life of your subscription',
                      'Direct line to the team building the product',
                      'A real voice in the roadmap and Field Advisor design',
                    ].map((b) => (
                      <li
                        key={b}
                        className="flex gap-3 font-grotesk text-charcoal/85 text-[15px] leading-relaxed"
                      >
                        <Check className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="md:justify-self-end">
                  <GetStartedDialog>
                    <Button className="bg-ink hover:bg-ink-soft text-cream rounded-full h-12 px-6 font-grotesk text-[15px]">
                      Become a Founding Partner <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </GetStartedDialog>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FAQ() {
  const faqs = [
    {
      q: 'How is this different from an engagement survey?',
      a: "A traditional engagement survey gives you a snapshot once or twice a year — and by the time the results come back, the moment has already passed. CultureLens is always on. You see what's happening every day, in real time, as your people share it. And because Charles, your Field Advisor, checks in conversationally, employees can expand on what they really mean, share new ideas, and think through how they actually feel about work — not just pick a number on a scale. Every conversation aggregates securely into themes and signals so you know what's shifting, where, and why — long before it shows up in retention, performance, or revenue.",
    },
    {
      q: 'Is it really anonymous?',
      a: "Yes — and that's the point. Individual conversations and pulses are never shown to anyone in the organization, including admins and executives. Charles talks with each person privately, and only aggregated patterns ever surface in reporting. Any view that would represent fewer than five people is suppressed, so leaders see culture, not individuals. Confidentiality isn't a setting — it's the foundation that makes honest answers possible.",
    },
    {
      q: 'How long does it take to set up?',
      a: "Most organizations are live in under two weeks. You define where you're taking your team, department, or organization, we configure Charles to your culture and language, and your people can start checking in the same week. No long rollout, no consulting engagement required to get value.",
    },
    {
      q: 'Who sees the results?',
      a: "Leaders see themes, trends, gaps, and recommendations — rolled up and aggregated across teams. They never see an individual's words, voice, or pulse. Employees see their own check-ins, their own trends over time, and the ideas they've contributed. Everyone gets clarity; no one loses privacy.",
    },
    {
      q: 'How do you turn conversations into action?',
      a: "Every conversation and pulse aggregates into living themes — what's energizing your people, what's getting in the way, and where the gap is widest between your stated culture and the lived one. Charles surfaces signals as they emerge, recommends where to focus, and tracks whether the changes you make are actually moving the dial. You go from insight to impact in weeks, not months.",
    },
    {
      q: 'Does it work for distributed teams?',
      a: "Yes — it's built for them. People check in with Charles on their own time, by voice or chat, from anywhere in the world. There's no scheduling, no facilitator, no town hall required. Distributed, hybrid, frontline, deskless — everyone gets the same private space to be heard, and you get one aggregated view of the whole culture.",
    },
  ];
  return (
    <section className="bg-cream py-24 md:py-32">
      <div className="max-w-content mx-auto px-6 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.2em] font-grotesk text-brand mb-4">
              Questions
            </p>
            <h2 className="font-display text-4xl md:text-5xl text-ink leading-[1.05]">
              Everything you'd ask in a first call.
            </h2>
          </Reveal>
        </div>
        <div className="md:col-span-8">
          <Reveal>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-hairline"
                >
                  <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-ink hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-grotesk text-charcoal/75 text-base leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const standard = [
    'Always-on daily pulses',
    'Charles, your AI Field Advisor, for conversational follow-up',
    'Real-time themes, signals, and gap analysis',
    'Anonymous by default, securely aggregated',
  ];
  const enterprise = [
    'Everything in the standard plan',
    'Multi-language and multi-persona Field Advisors',
    'Custom culture frameworks and segmentation',
    'Enterprise-grade data processing agreement, vendor security review support, and SSO on the roadmap.',
    'Dedicated customer success and onboarding',
    'Executive insight briefings',
  ];
  const workshops = [
    'One-off strategy and culture sessions, or an ongoing cadence',
    'Built around the themes Charles surfaces in your data',
    'Helps leaders build sharper plans — and stay accountable to them',
    'Delivered by senior IV Talent facilitators',
    'Available virtually or on-site',
  ];
  return (
    <section id="pricing" className="bg-cream-warm py-24 md:py-32">
      <div className="max-w-content mx-auto px-6">
        <Reveal>
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.2em] font-grotesk text-brand mb-4">
              Pricing
            </p>
            <h2 className="font-display text-4xl md:text-6xl text-ink leading-[1.05]">
              Simple, while we're
              <br />
              <span className="italic text-charcoal/60">getting to know each other.</span>
            </h2>
            <p className="mt-6 font-grotesk text-charcoal/75 text-lg leading-relaxed max-w-2xl">
              One price for now. Real conversations, daily pulses, Charles as your Field Advisor,
              secure aggregation, and the full insight layer — included.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-3 gap-6 md:gap-8 items-stretch">
          <Reveal>
            <div className="card-soft h-full rounded-2xl border border-hairline bg-white p-8 md:p-10 flex flex-col">
              <div className="text-[11px] uppercase tracking-[0.18em] font-grotesk text-charcoal/55 mb-6">
                Teams up to 200 currently
              </div>
              <div className="font-display text-5xl md:text-6xl text-ink leading-none">
                $14
                <span className="font-grotesk text-base text-charcoal/60 ml-2 align-middle">
                  / employee / month
                </span>
              </div>
              <p className="mt-3 font-grotesk text-sm text-charcoal/60">
                Billed annually.
              </p>
              <p className="mt-3 font-grotesk text-sm text-charcoal/70 leading-relaxed">
                Works beautifully for teams as small as seven — ideal for leadership groups and
                executive teams.
              </p>
              <ul className="mt-8 space-y-3 flex-1">
                {standard.map((b) => (
                  <li key={b} className="flex gap-3 font-grotesk text-charcoal/85 text-[15px] leading-relaxed">
                    <Check className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <GetStartedDialog>
                  <Button className="bg-ink hover:bg-ink-soft text-cream rounded-full h-12 px-6 font-grotesk text-[15px] w-full sm:w-auto">
                    Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </GetStartedDialog>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="card-soft h-full rounded-2xl border border-hairline bg-white p-8 md:p-10 flex flex-col relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-1.5 bg-brand" aria-hidden="true" />
              <div className="text-[11px] uppercase tracking-[0.18em] font-grotesk text-brand mb-6">
                Enterprise & teams over 200
              </div>
              <div className="font-display text-5xl md:text-6xl text-ink leading-none italic">
                Let's talk.
              </div>
              <p className="mt-3 font-grotesk text-sm text-charcoal/60">
                Custom pricing for organizations over 200 employees.
              </p>
              <ul className="mt-8 space-y-3 flex-1">
                {enterprise.map((b) => (
                  <li key={b} className="flex gap-3 font-grotesk text-charcoal/85 text-[15px] leading-relaxed">
                    <Check className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <GetStartedDialog>
                  <Button className="bg-ink hover:bg-ink-soft text-cream rounded-full h-12 px-6 font-grotesk text-[15px] w-full sm:w-auto">
                    Call us <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </GetStartedDialog>
              </div>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="card-soft h-full rounded-2xl border border-hairline bg-white p-8 md:p-10 flex flex-col relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 w-1 bg-brand" aria-hidden="true" />
              <div className="text-[11px] uppercase tracking-[0.18em] font-grotesk text-brand mb-3">
                Add-on · Advisory
              </div>
              <h3 className="font-display text-2xl md:text-3xl text-ink leading-snug mb-5">
                Facilitated Leadership Workshops
              </h3>
              <div className="font-display text-5xl md:text-6xl text-ink leading-none italic">
                Let's discuss.
              </div>
              <p className="mt-3 font-grotesk text-sm text-charcoal/60">
                One-off or recurring sessions, priced per engagement.
              </p>
              <p className="mt-5 font-grotesk text-charcoal/80 text-[15px] leading-relaxed">
                Insight only matters when leaders act on it. Our facilitators work with your
                leadership team to turn what CultureLens surfaces into plans that actually get
                executed.
              </p>
              <ul className="mt-6 space-y-3 flex-1">
                {workshops.map((b) => (
                  <li key={b} className="flex gap-3 font-grotesk text-charcoal/85 text-[15px] leading-relaxed">
                    <Check className="h-5 w-5 text-brand shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-10">
                <GetStartedDialog topic="workshops">
                  <Button className="bg-ink hover:bg-ink-soft text-cream rounded-full h-12 px-6 font-grotesk text-[15px] w-full sm:w-auto">
                    Let's discuss <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </GetStartedDialog>
              </div>
            </div>
          </Reveal>
        </div>

        <p className="mt-10 text-center font-grotesk text-sm text-charcoal/55 max-w-2xl mx-auto leading-relaxed">
          Software pricing is intentionally simple while we're in our founding chapter. Workshops
          are scoped to your team — current customers' platform pricing is protected as the product
          evolves.
        </p>

      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative bg-ink text-cream py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0 opacity-50">
        <FlowLine className="w-full h-full text-brand" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink/80 pointer-events-none" />
      <div className="relative max-w-content mx-auto px-6 text-center">
        <Reveal>
          <Waveform className="text-brand h-8 mx-auto mb-8" bars={11} />
          <h2 className="font-display text-4xl md:text-7xl leading-[1.05] max-w-4xl mx-auto">
            Ready to hear what your culture
            <br />
            <span className="italic">is actually saying?</span>
          </h2>
          <p className="mt-6 font-grotesk text-cream/70 text-lg md:text-xl">
            Real conversation. Real people. Real change.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <GetStartedDialog>
              <Button className="bg-brand hover:bg-brand/90 text-white rounded-full h-12 px-6 font-grotesk text-[15px]">
                Request a Demo <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </GetStartedDialog>
            <GetStartedDialog>
              <Button
                variant="ghost"
                className="rounded-full h-12 px-6 text-cream hover:text-cream hover:bg-white/10 border border-white/20 font-grotesk text-[15px]"
              >
                Talk to us
              </Button>
            </GetStartedDialog>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  const cols = [
    {
      title: 'Product',
      links: [
        { label: 'How it works', href: '#how' },
        { label: 'Privacy', href: '#privacy' },
        { label: 'Customers', href: '#customers' },
        { label: 'Sign in', href: '/auth/signin', internal: true },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'About IV Talent', href: '#' },
        { label: 'Contact', href: '/legal', internal: true },
        { label: 'Request a Demo', href: '#', cta: true },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Legal Notice', href: '/legal', internal: true },
        { label: 'Privacy Policy', href: '/privacy', internal: true },
      ],
    },
  ];

  return (
    <footer className="bg-cream border-t border-hairline pt-20 pb-10">
      <div className="max-w-content mx-auto px-6">
        <div className="grid md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-4">
            <Logo />
            <p className="mt-5 font-grotesk text-charcoal/70 max-w-xs leading-relaxed">
              Culture intelligence for people-first organizations.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-xs font-grotesk text-charcoal/60">
              <Waveform className="text-brand h-3" bars={5} />
              Real conversation. Real people. Real change.
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.title} className="md:col-span-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-grotesk text-charcoal/50 mb-4">
                {c.title}
              </div>
              <ul className="space-y-3">
                {c.links.map((l) =>
                  l.cta ? (
                    <li key={l.label}>
                      <GetStartedDialog>
                        <button className="font-grotesk text-charcoal hover:text-ink transition-colors text-left">
                          {l.label}
                        </button>
                      </GetStartedDialog>
                    </li>
                  ) : l.internal ? (
                    <li key={l.label}>
                      <Link
                        to={l.href}
                        className="font-grotesk text-charcoal hover:text-ink transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ) : (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="font-grotesk text-charcoal hover:text-ink transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-hairline pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs font-grotesk text-charcoal/55">
            © {new Date().getFullYear()} CultureLens by IV Talent. All rights reserved.
          </p>
          <p className="text-xs font-grotesk text-charcoal/55">
            Private by design · Anonymous by default
          </p>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------- */
/*  Page                                                          */
/* -------------------------------------------------------------- */

export default function Index() {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <div className="bg-cream text-ink font-grotesk antialiased">
      <Nav />
      <main>
        <Hero />
        
        <Problem />
        <Comparison />
        <Pillars />
        <HowItWorks />
        <Flexibility />
        <Privacy />
        <Stats />
        <Manifesto />
        <FAQ />
        <Pricing />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
